import React, { useEffect, useState, useRef } from "react";
import { Turnstile } from "@marsidev/react-turnstile";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate, useParams } from "react-router-dom";
import { buyCourse } from "../services/operations/studentFeaturesAPI";
import { fetchCourseDetails } from "../services/operations/courseDetailsAPI";
import RatingStars from "../common/RatingStars";
import GetAvgRating from "../utils/avgRating";
import Spinner from "../common/Spinner";
import { formatDate } from "../services/formatDate";
import { HiOutlineGlobeAlt } from "react-icons/hi";
import { BiInfoCircle } from "react-icons/bi";
import CourseDetailsCard from "../components/core/Course/CourseDetailsCard";
import ComnfirmationModal from "../common/ComnfirmationModal";
import Error from "./Error";
import CourseAccordionBar from "../components/core/Course/CourseAccordionBar";
import Footer from "../common/Footer";
import { ACCOUNT_TYPE } from "../utils/constants";
import toast from "react-hot-toast";
import { addTocart } from "../services/operations/cartAPI";
import { setTotalItems } from "../slices/cartSlice";
import { FaArrowRight, FaCheck, FaStar } from "react-icons/fa";
import "./CourseRelated.css";
import mentorFallback from "../assets/Images/VikasBagariaMain.png";
import { getBillingInfoError } from "../utils/billingValidation";

const RelatedCourseCard = ({ course }) => {
  const rating = GetAvgRating(course?.ratingAndReviews || []);
  const instructorName = `${course?.instructor?.firstName || ""} ${
    course?.instructor?.lastName || ""
  }`.trim();

  return (
    <Link to={`/courses/${course._id}`} className="cnt-related-card">
      <div className="cnt-related-image">
        <img src={course.thumbnail} alt={course.courseName} />
        <span>{course.courseType}</span>
      </div>
      <div className="cnt-related-body">
        {instructorName && <small>By {instructorName}</small>}
        <h3>{course.courseName}</h3>
        <p>{course.courseDescription}</p>
        <div className="cnt-related-rating">
          <FaStar />
          <b>{rating.toFixed(1)}</b>
          <span>({course?.ratingAndReviews?.length || 0})</span>
          <em>{course?.studentsEntrolled?.length || 0} learners</em>
        </div>
        <div className="cnt-related-bottom">
          <strong>₹{course.price}</strong>
          <span>View course <FaArrowRight /></span>
        </div>
      </div>
    </Link>
  );
};

const CourseDetails = () => {
  const { token } = useSelector((state) => state.auth);
  const { user } = useSelector((state) => state.profile);
  const { loading } = useSelector((state) => state.profile);
  const { totalItems } = useSelector((state) => state.cart);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { courseId } = useParams();

  const [courseData, setCourseData] = useState(null);
  const [avgReviewCount, setAvgReviewCount] = useState(0);
  const [confirmationModal, setConfirmationModal] = useState(null);
  let [totalNumberOfLectures, setTotalNumberOfLectures] = useState(0);
  const [isActive, setIsActive] = useState(Array(0));
  const [isExpanded, setIsExpanded] = useState(false);
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const contentRef = useRef(null);

  const [captchaToken, setCaptchaToken] = useState(null);
  const [showBilling, setShowBilling] = useState(false);
  const [mobileBilling, setMobileBilling] = useState({
    nameOnInvoice: "",
    email: user?.email || "",
    mobile: "",
    addressLine1: "",
    addressLine2: "",
    addressLine3: "",
    placeOfSupply: "",
    pincode: "",
  });

  // Something extra start
  // Get cartItems
  // const [cartItem, setCartItem] = useState(null);
  // useEffect(() => {
  //   const fetchCartItems = async () => {
  //     const cartDetails = await getEntireCart(token);
  //     setCartItem(cartDetails);
  //     console.log("cartDetails", cartDetails);
  //   };
  //   fetchCartItems();
  //   // eslint-disable-next-line
  // }, [courseId]);
  // Something extra end

  const handleActive = (id) => {
    setIsActive(
      !isActive.includes(id)
        ? isActive.concat([id])
        : isActive.filter((e) => e !== id),
    );
  };

  // Adding details for course purchase
  const handleBuyCourse = async (
    billingInfo,
    captchaToken,
    setCaptchaToken,
  ) => {
    if (token) {
      await buyCourse(
        token,
        [courseId],
        user,
        navigate,
        dispatch,
        appliedCoupon,
        billingInfo,
        captchaToken,
        setCaptchaToken,
      );
      return;
    }
    setConfirmationModal({
      text1: "You are not logged in!",
      text2: "Please login to Purchase Course",
      btn1Text: "Login",
      btn2Text: "Cancel",
      btn1Handler: () => navigate("/login"),
      btn2Handler: () => setConfirmationModal(null),
    });
  };

  useEffect(() => {
    // Fetch the details of the corse to be shown
    const getCourseDetails = async () => {
      try {
        const res = await fetchCourseDetails(courseId);
        setCourseData(res);
      } catch (error) {
        console.log(error);
      }
    };

    // Call the function to get data
    getCourseDetails();
  }, [courseId]);

  // Avg review
  useEffect(() => {
    const count = GetAvgRating(
      courseData?.data?.courseDetails?.ratingAndReviews,
    );
    setAvgReviewCount(count);
  }, [courseData]);

  // Total number of lectures
  useEffect(() => {
    let lectures = 0;
    courseData?.data?.courseDetails?.courseContent.forEach((sec) => {
      lectures += sec.subSection.length || 0;
    });
    setTotalNumberOfLectures(lectures);
  }, [courseData]);

  if (loading || !courseData) {
    return (
      <div className="grid min-h-[calc(100vh-3.5rem)] place-items-center">
        <Spinner />
      </div>
    );
  }
  if (!courseData.success) {
    return <Error />;
  }

  const handleAddToCart = async () => {
    // Check if the instructor is trying to buy a course
    // If so, show error
    if (user && user?.accountType === ACCOUNT_TYPE.INSTRUCTOR) {
      toast.error("You are Instructor. You can't buy a course");
      return;
    }

    let courseID = JSON.parse(JSON.stringify({ _id: courseId }));
    // if the user is logged in then dispatch add to cart else asl to login first
    if (token) {
      const res = await addTocart({ courseId: courseID }, token);
      if (res) {
        dispatch(setTotalItems(totalItems + 1));
      }
      return;
    }
    setConfirmationModal({
      text1: "You are not logged in!",
      text2: "Please login to add courses to your cart",
      btn1Text: "Login",
      btn2Text: "Cancel",
      btn1Handler: () => navigate("/login"),
      btn2Handler: () => setConfirmationModal(null),
    });
  };

  const {
    courseName,
    courseDescription,
    thumbnail,
    price,
    whatYouWillLearn,
    courseContent,
    ratingAndReviews,
    instructor,
    studentsEntrolled,
    createdAt,
    category,
    courseType,
  } = courseData?.data?.courseDetails;
  const relatedCourses = courseData?.data?.relatedCourses || [];
  const instructorFullName =
    `${instructor.firstName || ""} ${instructor.lastName || ""}`.trim();
  const isVikashBagaria = /vikash?\s+bagaria/i.test(instructorFullName);

  const learnPoints = whatYouWillLearn
    .split(".")
    .map((point) => point.trim())
    .filter((point) => point.length > 0);

  const isEnrolled = user && studentsEntrolled.includes(user._id);

  return (
    <>
      <div className="relative w-full bg-richblack-800">
        {/* Hero Section */}
        <div className="mx-auto box-content px-4 lg:w-[1260px] 2xl:relative">
          <div className="mx-auto min-h-[450px] py-8 lg:mx-0 lg:py-0 xl:max-w-[810px]">
            <div className="pb-7 lg:pb-0 pt-0 lg:pt-7 text-richblack-300 h-fit">
              Home / Learning /{" "}
              <span className="text-yellow-50">{`${category.name}`}</span>
            </div>

            {/* Image */}
            <div className="block lg:hidden relative max-h-[30rem]">
              <div className="absolute bottom-0 left-0 h-full w-full shadow-[#161D29_0px_-64px_36px_-28px_inset]"></div>
              <img
                src={thumbnail}
                alt={courseName}
                className="w-full aspect-square object-cover rounded-md max-h-[30rem] lg:max-h-[200px]"
              />
            </div>

            <div className=" flex flex-col items-center lg:items-start justify-center gap-4 text-lg text-richblack-5 py-5">
              {/* CourseName */}
              <p className="text-3xl font-bold text-richblack-5 sm:text-5xl">
                {courseName}
              </p>

              {/* Course Description */}
              <p className="text-richblack-200">{courseDescription}</p>

              {/* Ratings section */}
              <div className="flex text-md flex-wrap items-center gap-2">
                {/* Avg Review */}
                <span className="text-yellow-25">{avgReviewCount}</span>
                {/* Stars */}
                <RatingStars Review_Count={avgReviewCount} Star_Size={24} />
                {/* Total reviews */}
                <span>{`(${ratingAndReviews.length} reviews)`}</span>
                {/* Students enrolled */}
                <span>{`${studentsEntrolled.length} students enrolled`}</span>
              </div>

              {/* Intructor Name */}
              <p>
                Created By {`${instructor.firstName} ${instructor.lastName}`}
              </p>

              {/* Created At */}
              <div className="flex flex-wrap gap-5 text-lg">
                <div className="flex items-center gap-2">
                  <BiInfoCircle />
                  <p> Created at {formatDate(createdAt).split("|").at(0)}</p>
                </div>
                <div className="flex items-center gap-2">
                  <HiOutlineGlobeAlt />
                  <p>English</p>
                </div>
              </div>
            </div>
            {/* checkout Buttons for small screen */}
            <div className="lg:hidden flex flex-col w-full gap-4 border-y border-y-richblack-500 py-4">
              {!isEnrolled && (
                <>
                  {/* Toggle Header */}
                  <div
                    onClick={() => setShowBilling((prev) => !prev)}
                    className="cursor-pointer flex justify-between items-center bg-richblack-700 px-4 py-3 rounded-md"
                  >
                    <p className="text-richblack-5 font-medium">
                      Enter Billing Details
                    </p>
                    <span className="text-yellow-50">
                      {showBilling ? "▲" : "▼"}
                    </span>
                  </div>
                  <div
                    className={`transition-all duration-300 overflow-hidden ${
                      showBilling ? "max-h-[500px] mt-3" : "max-h-0"
                    }`}
                  >
                    <div className="flex flex-col gap-3">
                      <input
                        type="email"
                        className="p-2 rounded bg-richblack-800 border border-richblack-600 text-white"
                        placeholder="Email Address"
                        value={mobileBilling.email}
                        onChange={(e) => setMobileBilling({ ...mobileBilling, email: e.target.value })}
                      />

                      <input
                        type="tel"
                        inputMode="numeric"
                        maxLength={10}
                        className="p-2 rounded bg-richblack-800 border border-richblack-600 text-white"
                        placeholder="Mobile Number"
                        value={mobileBilling.mobile}
                        onChange={(e) => setMobileBilling({ ...mobileBilling, mobile: e.target.value.replace(/\D/g, "") })}
                      />

                      <input
                        className="p-2 rounded bg-richblack-800 border border-richblack-600 text-white"
                        placeholder="Name"
                        value={mobileBilling.nameOnInvoice}
                        onChange={(e) =>
                          setMobileBilling({
                            ...mobileBilling,
                            nameOnInvoice: e.target.value,
                          })
                        }
                      />

                      <input
                        type="tel"
                        inputMode="numeric"
                        maxLength={6}
                        className="p-2 rounded bg-richblack-800 border border-richblack-600 text-white"
                        placeholder="PIN Code"
                        value={mobileBilling.pincode}
                        onChange={(e) => setMobileBilling({ ...mobileBilling, pincode: e.target.value.replace(/\D/g, "") })}
                      />

                      <input
                        className="p-2 rounded bg-richblack-800 border border-richblack-600 text-white"
                        placeholder="Address Line 1"
                        value={mobileBilling.addressLine1}
                        onChange={(e) =>
                          setMobileBilling({
                            ...mobileBilling,
                            addressLine1: e.target.value,
                          })
                        }
                      />

                      <input
                        className="p-2 rounded bg-richblack-800 border border-richblack-600 text-white"
                        placeholder="Address Line 2 (Optional)"
                        value={mobileBilling.addressLine2}
                        onChange={(e) =>
                          setMobileBilling({
                            ...mobileBilling,
                            addressLine2: e.target.value,
                          })
                        }
                      />

                      <input
                        className="p-2 rounded bg-richblack-800 border border-richblack-600 text-white"
                        placeholder="Address Line 3 (Optional)"
                        value={mobileBilling.addressLine3}
                        onChange={(e) =>
                          setMobileBilling({
                            ...mobileBilling,
                            addressLine3: e.target.value,
                          })
                        }
                      />

                      <input
                        className="p-2 rounded bg-richblack-800 border border-richblack-600 text-white"
                        placeholder="State"
                        value={mobileBilling.placeOfSupply}
                        onChange={(e) =>
                          setMobileBilling({
                            ...mobileBilling,
                            placeOfSupply: e.target.value,
                          })
                        }
                      />

                      {/* CAPTCHA */}
                      <Turnstile
                        siteKey={process.env.REACT_APP_TURNSTILE_SITE_KEY}
                        onSuccess={(token) => setCaptchaToken(token)}
                        onExpire={() => setCaptchaToken(null)}
                        onError={() => {
                          setCaptchaToken(null);
                          toast.error("Human verification could not load. Please refresh and try again.");
                        }}
                      />
                    </div>
                  </div>
                </>
              )}

              <p className="text-3xl font-semibold text-richblack-5">
                Rs. {price}
              </p>
              <button
                onClick={() => {
                  if (user && studentsEntrolled.includes(user._id)) {
                    navigate("/dashboard/enrolled-courses");
                    return;
                  }

                  if (!showBilling) {
                    setShowBilling(true);
                    toast("Please fill billing details");
                    return;
                  }

                  const billingError = getBillingInfoError(mobileBilling);
                  if (billingError) {
                    toast.error(billingError);
                    return;
                  }

                  if (!captchaToken) {
                    toast.error("Please verify you are human");
                    return;
                  }

                  handleBuyCourse(mobileBilling, captchaToken, setCaptchaToken);
                }}
                className="cursor-pointer w-full px-4 py-2 bg-yellow-50 hover:scale-105 rounded-md text-richblack-800"
              >
                {user && studentsEntrolled.includes(user._id)
                  ? "Go To Course"
                  : "Buy Now"}
              </button>
              {(!user || !studentsEntrolled.includes(user._id)) && (
                <button
                  onClick={handleAddToCart}
                  className="cursor-pointer w-full px-4 py-2 bg-richblack-800 hover:scale-105 rounded-md text-richblack-5 hover:text-yellow-50"
                >
                  Add to Cart
                </button>
              )}
            </div>
          </div>

          {/* vertical line */}
          {/* <div className=" hidden xl:block h-[1px] rotate-90 w-[300px] absolute ml-[calc(100vw-70%)] top-52 bg-richblack-600"></div> */}

          {/* Card Section, Buy Now, Add to cart */}
          <div className="hidden lg:block absolute mx-auto min-h-[600px] w-1/3 max-w-[410px] top-[60px] right-[1rem] ">
            <CourseDetailsCard
              course={courseData?.data?.courseDetails}
              handleBuyCourse={handleBuyCourse}
              setConfirmationModal={setConfirmationModal}
              setAppliedCoupon={setAppliedCoupon}
            />
          </div>
        </div>
      </div>

      <div className="mx-auto box-content px-4 lg:w-[1260px] text-start text-richblack-5">
        <div className="mx-auto max-w-maxContentTab lg:mx-0 xl:max-w-[810px]">
          {/* What you will learn */}
          <div className="my-8 border border-richblack-600 px-6 pt-6 pb-2 relative overflow-hidden">
            <p className="text-3xl font-semibold mb-5">What you'll learn</p>

            {/* Collapsible content */}
            <div
              ref={contentRef}
              className={`grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 transition-[max-height] duration-500 ease-in-out overflow-hidden`}
              style={{
                maxHeight: isExpanded
                  ? `${contentRef.current?.scrollHeight}px`
                  : "200px",
              }}
            >
              {learnPoints.map((point, index) => (
                <div
                  key={index}
                  className="flex items-start gap-3 text-richblack-100"
                >
                  <FaCheck className="text-caribbeangreen-400 mt-1 shrink-0" />
                  <span>{point}.</span>
                </div>
              ))}
            </div>

            {/* Gradient fade + Button */}
            {!isExpanded && (
              <div className="absolute bottom-0 left-0 w-full h-[80px] bg-gradient-to-t from-richblack-800 to-transparent pointer-events-none" />
            )}

            <span className="mt-3 flex justify-start">
              <button
                onClick={() => setIsExpanded((prev) => !prev)}
                className="text-yellow-50 underline text-sm font-semibold z-10"
              >
                {isExpanded ? "Show Less" : "Show More"}
              </button>
            </span>
          </div>

          {/* Course Content Section */}
          <div className="max-w-[830px]">
            <div className="flex flex-col gap-3">
              <p className="text-3xl font-semibold">Course Content</p>
              <div className="flex flex-wrap justify-between gap-2">
                <div className="flex gap-2 text-richblack-100">
                  <span>
                    {courseContent.length} {`section(s)`}
                  </span>
                  <span>
                    {totalNumberOfLectures} {`lecture(s)`}{" "}
                  </span>
                  <span>
                    {courseData?.data?.totalDuration} {`total length`}{" "}
                  </span>
                </div>
                <div>
                  <button
                    onClick={() => setIsActive([])}
                    className="text-yellow-25"
                  >
                    Collapse all sections
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Course Details accordion */}
          <div className="py-4">
            {courseContent.map((course, index) => (
              <CourseAccordionBar
                course={course}
                key={index}
                isActive={isActive}
                handleActive={handleActive}
                sectionIndex={index}
              />
            ))}
          </div>

          {/* Mentor Details */}
          <section className="cnt-course-mentor-section">
            <div className="cnt-course-section-label">ABOUT THE MENTOR</div>
            <div className="cnt-course-mentor-profile">
              <div className="cnt-course-mentor-photo">
                <img
                  alt={instructorFullName}
                  src={
                    instructor.image
                      ? instructor.image
                      : isVikashBagaria
                        ? mentorFallback
                      : `https://api.dicebear.com/5.x/initials/svg?seed=${instructor.firstName} ${instructor.lastName}`
                  }
                />
              </div>
              <div>
                <span>
                  {isVikashBagaria
                    ? "Founder & Market Educator"
                    : "CNT Academy Market Educator"}
                </span>
                <h2>{instructorFullName}</h2>
                <p>
                  {instructor?.additionalDetails?.about ||
                    `${instructor.firstName} guides learners through practical market concepts, structured analysis, and risk-aware decision-making. Each lesson focuses on understanding the reasoning behind a concept and applying it with clarity and discipline.`}
                </p>
                <div className="cnt-mentor-values">
                  <span><FaCheck /> Practical explanations</span>
                  <span><FaCheck /> Structured learning</span>
                  <span><FaCheck /> Risk-first approach</span>
                </div>
              </div>
            </div>
          </section>
        </div>
      </div>

      {relatedCourses.length > 0 && (
        <section className="cnt-related-courses">
          <div className="cnt-related-wrap">
            <div className="cnt-related-heading">
              <div>
                <span>KEEP LEARNING</span>
                <h2>Other {courseType} courses</h2>
                <p>
                  Continue with courses designed for the same learning level.
                </p>
              </div>
              <Link to={`/catalog/${category.name}`}>
                Browse all courses <FaArrowRight />
              </Link>
            </div>
            <div className="cnt-related-grid">
              {relatedCourses.map((course) => (
                <RelatedCourseCard key={course._id} course={course} />
              ))}
            </div>
          </div>
        </section>
      )}

      <Footer />
      {/* <button
        className="bg-yellow-50 px-6 py-4"
        onClick={() => handleBuyCourse()}
      >
        Buy Now
      </button> */}

      {confirmationModal && (
        <ComnfirmationModal modalData={confirmationModal} />
      )}
    </>
  );
};

export default CourseDetails;
